import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, MotionValue, useTransform } from "framer-motion";
import { Volume2Icon } from "lucide-react";
import type { Landmark, LandmarkSlot } from "../../types/courseTheme";
import { LandmarkArt } from "./LandmarkArt";
import { speak } from "../../utils/speech";
interface LandmarkFigureProps {
  landmark: Landmark;
  slot: LandmarkSlot;
  speechLang: string;
  accent: string;
  accent2: string;
  pointerX: MotionValue<number>;
  pointerY: MotionValue<number>;
  scrollY: MotionValue<number>;
  reduce: boolean;
  /** The flashlight copy: tinted, non-interactive, drawn exactly on top of the base copy. */
  lit?: boolean;
  onDiscover?: () => void;
}
const ease = [0.23, 1, 0.32, 1] as const;
export function LandmarkFigure({
  landmark,
  slot,
  speechLang,
  accent,
  accent2,
  pointerX,
  pointerY,
  scrollY,
  reduce,
  lit = false,
  onDiscover
}: LandmarkFigureProps) {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const timer = useRef<number>();

  // Gentle depth: pointer parallax plus a slow drift as the path scrolls.
  const x = useTransform(pointerX, v => reduce ? 0 : v * 12 * slot.depth);
  const py = useTransform(pointerY, v => reduce ? 0 : v * 10 * slot.depth);
  const sy = useTransform(scrollY, v => reduce ? 0 : -v * 0.04 * slot.depth);
  const y = useTransform([py, sy], ([a, b]: number[]) => a + b);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const position: React.CSSProperties = {
    top: `${slot.top}%`,
    [slot.side]: slot.edge,
    width: `clamp(130px, ${(slot.width / 14.4).toFixed(2)}vw, ${slot.width}px)`,
    aspectRatio: "1 / 1"
  };
  if (lit) {
    return <motion.div className="absolute" style={{
      ...position,
      x,
      y
    }} data-id="element-778" data-mp="3m6jai3vylrjt">
        <LandmarkArt name={landmark.art} a={`rgb(${accent} / 0.22)`} b={`rgb(${accent2} / 0.3)`} className="h-full w-full" data-id="element-779" data-mp="3z3jxw110n1l" />
      </motion.div>;
  }
  const showLabel = hovered || pinned;
  const onClick = () => {
    speak(landmark.name, speechLang);
    onDiscover?.();
    setPinned(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setPinned(false), 2400);
  };
  return <motion.div className="pointer-events-auto absolute" style={{
    ...position,
    x,
    y
  }} data-id="element-780" data-mp="36j3rs2h8irs3">
      <button type="button" onClick={onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} onFocus={() => setHovered(true)} onBlur={() => setHovered(false)} aria-label={`${landmark.name}, ${landmark.place}. Hear its name`} className="group block h-full w-full cursor-pointer rounded-2xl text-ink outline-none focus-visible:ring-2 focus-visible:ring-brand/40" data-id="element-781" data-mp="3biggksro28xb">
        <LandmarkArt name={landmark.art} a={`rgb(${accent} / 0.55)`} b={`rgb(${accent2} / 0.55)`} className={`h-full w-full transition-opacity duration-200 ease-out ${showLabel ? "opacity-40" : "opacity-[0.13]"}`} data-id="element-782" data-mp="1r4g462pa8i5n" />
      </button>

      <AnimatePresence data-id="element-783" data-mp="1is4nu9994rij">
        {showLabel && <span className="pointer-events-none absolute left-1/2 top-full z-10 -mt-1 -translate-x-1/2" data-id="element-784" data-mp="1dtuu8336dyz1">
            <motion.span role="status" initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: -4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          transition: {
            duration: 0.12
          }
        }} transition={{
          duration: 0.18,
          ease
        }} className="flex items-center gap-2 whitespace-nowrap rounded-xl border-2 border-line/40 bg-surface/95 px-3 py-1.5 text-sm shadow-brutal-sm" data-id="element-785" data-mp="19tkbdpuabe2r">
              <Volume2Icon className="h-3.5 w-3.5 text-ink-2" aria-hidden="true" data-id="element-786" data-mp="1slliffh7qan9" />
              <span className="font-bold text-ink" lang={speechLang} data-id="element-787" data-mp="m4hy0u6oo7e2">
                {landmark.name}
              </span>
              <span className="text-ink-2" data-id="element-788" data-mp="h6gb1y64znxb">· {landmark.place}</span>
            </motion.span>
          </span>}
      </AnimatePresence>
    </motion.div>;
}